import React, { useCallback } from 'react';
import { Card, Col, Row, Table, Tooltip as AntdTooltip } from 'antd';
import moment from 'moment';
import { exportAnalyticsPageAsPdf } from '../../utils/AnalyticsPdfExportUtils';
import AnalyticsControlBar from '../analytics_control_bar/AnalyticsControlBar';
import { VIEW_MODE } from './LifeCycleAnalyticsConstants';
import { getLifecycleDateRanges } from './LifeCycleAnalyticsUtils';
import { EmptyState } from '../empty_state/EmptyState';
import PriorityCardScroller from './PriorityCardScroller';

const PDF_EXPORT_ROOT_SELECTOR = '.lifeCycleAnalyticsOverviewPage';

const LIFECYCLE_FILTER_CONFIG = {
	problemTypes: true,
	isPM: true,
	workCreatedAt: true,
	isSupplierAnalytics: true,
};

const priorityTableRowKey = (row: { woPriorityId: number | string }) => `${row.woPriorityId}`;

const PrioritySummaryCard = ({
	loading,
	totalCompletedWorkOrders,
	prioritySummaryCards,
	tableDataSource,
	priorityTableColumns,
	viewMode,
	selectedPriorityId,
	handleRowClick,
	handleCardClick,
	exportPrioritySummaryCsv,
	controlBarProps,
}) => {
	const isDetailView = viewMode !== VIEW_MODE.ALL;
	const { filterValues } = controlBarProps;
	const handleExportPdf = useCallback(
		() =>
			exportAnalyticsPageAsPdf(
				PDF_EXPORT_ROOT_SELECTOR,
				'life-cycle-analytics-' + new Date().toISOString().slice(0, 10) + '.pdf'
			),
		[]
	);

	return (
		<Row gutter={12} className="-mx-1.5 my-3">
			<Col span={24}>
				<div className="mb-4">
					<AnalyticsControlBar
						userType={controlBarProps.userType}
						updateFilters={controlBarProps.updateFilters}
						startDate={filterValues.startDate}
						endDate={filterValues.endDate}
						dateRangeLabel="Completed Between"
						supplierFacilityIds={filterValues.supplierFacilityIds}
						problemTypeIds={filterValues.problemTypeIds}
						isPM={filterValues.isPM}
						workCreatedAtStartDate={filterValues.workCreatedAtStartDate}
						workCreatedAtEndDate={filterValues.workCreatedAtEndDate}
						dateRangePickerProps={{
							ranges: getLifecycleDateRanges(),
							disabledEnd: moment().endOf('day'),
						}}
						filterConfig={LIFECYCLE_FILTER_CONFIG}
						hideLocationsControl={true}
						hideSuppliersControl={false}
						showSearch={false}
						showDownload
						onDownload={exportPrioritySummaryCsv}
						showPrint
						onPrint={handleExportPdf}
						filterWrapperClassname="flex-1"
						showOnlyIcon
						additionalConfigs={
							<>
								<div className="h4medium order-first mr-3 text-character-primary">
									Completed Work Orders: {totalCompletedWorkOrders}
								</div>
							</>
						}
					/>
				</div>
				{!loading && totalCompletedWorkOrders === 0 ? (
					<Card className="lifeCycleSummaryCard">
						<EmptyState
							height={200}
							headline={'No completed work orders found'}
							body={
								<div className="max-w-[488px] text-center">
									There are no completed work orders for the selected filters. Try adjusting your
									filters or expanding the date range.
								</div>
							}
						/>
					</Card>
				) : isDetailView ? (
					<PriorityCardScroller
						cards={prioritySummaryCards}
						selectedPriorityId={selectedPriorityId}
						onCardClick={handleCardClick}
					/>
				) : (
					<Card className="lifeCycleSummaryCard">
						<div className="lifeCyclePriorityTable">
							<Table
								columns={priorityTableColumns}
								dataSource={tableDataSource}
								pagination={false}
								size="small"
								tableLayout="fixed"
								rowKey={priorityTableRowKey}
								onRow={handleRowClick}
							/>
						</div>
					</Card>
				)}
			</Col>
			{viewMode === VIEW_MODE.ALL && (
				<Col span={24} className="bodyregular px-4 py-3 text-center text-character-secondary">
					Changes shown are measured against previous period
				</Col>
			)}
		</Row>
	);
};

export default PrioritySummaryCard;
